// PATH: src/context/AuthContext.tsx — état de session de l'application.
//
// Aligné sur tc_tilmicode_ui (context/AuthContext + routes/ProtectedRoute), en
// plus simple : ni métadonnées, ni synchronisation inter-onglets — TilmiSchool
// n'a besoin que de savoir si la session est ouverte.
//
// La session vit dans le cookie httpOnly tc_session, que JavaScript ne peut PAS
// lire : le seul moyen de savoir si l'on est connecté est de demander /me à
// tc_identity. D'où le statut « unknown » tant que la réponse n'est pas revenue.
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAuthApi, type CoreUser } from "../api/auth";
import { onUnauthorized } from "../api/authEvents";

export type AuthStatus = "unknown" | "authenticated" | "anonymous";

interface AuthContextValue {
  status: AuthStatus;
  user: CoreUser | null;
  /** true tant que la session n'a pas été vérifiée au moins une fois. */
  isLoading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth doit être utilisé dans <AuthProvider>");
  return ctx;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const api = useAuthApi();
  const [status, setStatus] = useState<AuthStatus>("unknown");
  const [user, setUser] = useState<CoreUser | null>(null);

  const refresh = useCallback(async (): Promise<boolean> => {
    try {
      const me = await api.me();
      setUser(me);
      setStatus("authenticated");
      return true;
    } catch {
      // 401 (session absente/expirée) comme panne réseau ou CORS : dans les deux
      // cas la session n'est PAS vérifiable, donc on n'ouvre pas l'application.
      // Rediriger vers /login est le seul comportement sûr — et l'écran de
      // connexion affichera l'erreur réelle si le serveur est injoignable.
      setUser(null);
      setStatus("anonymous");
      return false;
    }
  }, [api]);

  // Vérification unique au démarrage. `api` est mémoïsé (useApiClient mémoïse
  // l'instance axios) : cet effet ne se relance pas à chaque rendu.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Session expirée en cours d'utilisation : n'importe quelle requête peut
  // recevoir un 401. On repasse à « anonymous » sans rappeler /me — le serveur
  // vient précisément de dire que la session ne vaut plus rien.
  useEffect(() => onUnauthorized(() => {
    setUser(null);
    setStatus("anonymous");
  }), []);

  const login = useCallback(
    async (username: string, password: string): Promise<boolean> => {
      const ok = await api.loginBasic(username, password);
      if (!ok) return false;
      // On relit /me plutôt que de supposer la session ouverte : c'est ce qui
      // met `status` à jour. Sans cela, le garde de route verrait encore
      // « anonymous » juste après la connexion et renverrait vers /login.
      return refresh();
    },
    [api, refresh]
  );

  const logout = useCallback(async (): Promise<void> => {
    await api.logout();
    setUser(null);
    setStatus("anonymous");
  }, [api]);

  const value = useMemo(
    () => ({ status, user, isLoading: status === "unknown", login, logout }),
    [status, user, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
